import {createSelector} from 'reselect';

import {findBadgeCounter, findBoldCounter, findDelegationBadgeCounter} from '../../../helpers/counterHelpers';
import {isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {CountersVisibilityModesEnum} from '../../../models/api/preferencesModel';
import {getBoldCounters, getCounters} from '../dataSelectors';
import {getUserNamespace} from '../teammates/teammatesSelectors';
import {
  getBadgeNamespace,
  getCountersVisibilities,
  getShouldUseOpenFilterForTeams,
} from './preferencesSelectors';

type GetCountersVisibilityNamespace = AppParametricSelector<any, string>;
export function makeGetCountersVisibility(getCountersVisibilityNamespace: GetCountersVisibilityNamespace) {
  return createSelector(
    [getCountersVisibilityNamespace, getCountersVisibilities, getShouldUseOpenFilterForTeams],
    (namespace, countersVisibilities, shouldShowTeammateTabsInTeamContext) => {
      const visibilityForNamespace = countersVisibilities[namespace];

      // If we're in a teammate namespace, show the visibility for that namespace, or the open one.
      if (isTeammateNamespace(namespace)) {
        return visibilityForNamespace || CountersVisibilityModesEnum.OPEN;
      }

      // If we're in a team namespace and we have the new filters the visibility depends on a preference.
      return shouldShowTeammateTabsInTeamContext
        ? CountersVisibilityModesEnum.OPEN
        : CountersVisibilityModesEnum.UNASSIGNED;
    },
  );
}

/*
 * Workspaces badge.
 */

export interface CountersBadgeProps {
  counterKey?: string;
  counterDelegationId?: number;
  countersVisibility?: CountersVisibilityModesEnum;
}

export function getCounterKey(_state: unknown, props: CountersBadgeProps) {
  return props.counterKey;
}

export function getCounterDelegationId(_state: unknown, props: CountersBadgeProps) {
  return props.counterDelegationId;
}

export function getCountersVisibility(_state: unknown, props: CountersBadgeProps) {
  return props.countersVisibility || CountersVisibilityModesEnum.OPEN;
}

type CounterBadgeValueSelector<P extends CountersBadgeProps> = AppParametricSelector<P, number>;
/** Find whether this item is unread from the specified counter key and visibility. */
export function makeGetCounterIsUnread<P extends CountersBadgeProps>(
  getBadgeValue: CounterBadgeValueSelector<P>,
) {
  return createSelector(
    [getBoldCounters, getCountersVisibility, getCounterKey, getCounterDelegationId, getBadgeValue],
    (boldCounters, visibility, key, delegationId, badgeValue) => {
      if (!key || delegationId || badgeValue === 0) {
        return false;
      }

      return findBoldCounter(boldCounters, visibility, key);
    },
  );
}

/** Find the badge value for the specified counter key and visibility. */
export function makeGetCounterBadgeValue<P extends CountersBadgeProps>(): CounterBadgeValueSelector<P> {
  return createSelector(
    [getCounters, getCountersVisibility, getCounterKey, getCounterDelegationId],
    (counters, visibility, key, delegationId) => {
      if (!key) {
        return 0;
      }

      // If we have a delegation, use its counter.
      if (delegationId) {
        return findDelegationBadgeCounter(counters, delegationId, key);
      }

      // Otherwise, use the global counters.
      return findBadgeCounter(counters, visibility, key);
    },
  );
}

/*
 * App icon badge.
 */

export const getBadgeValue = createSelector(
  [getBadgeNamespace, getUserNamespace, getCounters, getCountersVisibilities, getShouldUseOpenFilterForTeams],
  (badgeNamespace, userNamespace, counters, countersVisibilities, shouldUseOpenFilterForTeams) => {
    // No badge namespace => no badge to display.
    if (!badgeNamespace) {
      return 0;
    }

    // If this is the user namespace, display our Inbox counter.
    if (badgeNamespace === userNamespace) {
      const counterKey = 'self_inbox';
      const visibility = countersVisibilities[userNamespace] || CountersVisibilityModesEnum.OPEN;
      const badge = findBadgeCounter(counters, visibility, counterKey);
      return badge;
    }

    // Otherwise, this is a team counter.
    const visibility = shouldUseOpenFilterForTeams
      ? CountersVisibilityModesEnum.OPEN
      : CountersVisibilityModesEnum.UNASSIGNED;
    const counterKey = `${badgeNamespace}:${visibility}`;
    const badge = findBadgeCounter(counters, visibility, counterKey);
    return badge;
  },
);
